import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Store, Search, Minus, Plus, CreditCard, Banknote, ShoppingCart, X, Trash2 } from "lucide-react";
import { formatApiErrorDetail } from "../context/AuthContext";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GelAlPage() {
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(null);
  const [query, setQuery] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [searchParams] = useSearchParams();

  const [menu, setMenu] = useState(null);
  const [cart, setCart] = useState({}); // id -> { qty, drink, removed[] }
  const [openOptions, setOpenOptions] = useState({}); // id -> "drink" | "removed" | null
  const [activeCat, setActiveCat] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [modalDraft, setModalDraft] = useState({ qty: 1, drink: "", removed: [] });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [placing, setPlacing] = useState(false);

  const loadBranches = (attempt = 0) => {
    setLoadState("loading");
    axios
      .get(`${API}/branches`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setBranches(res.data);
          setLoadState("ok");
          const pre = searchParams.get("sube");
          if (pre) {
            const found = res.data.find((b) => b.code.toLowerCase() === pre.toLowerCase());
            if (found) setBranch(found);
          }
        }
      })
      .catch(() => {
        if (attempt < 3) setTimeout(() => loadBranches(attempt + 1), 3000);
        else setLoadState("error");
      });
  };

  useEffect(() => {
    loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (branch && !menu) {
      axios
        .get(`${API}/link-menu`)
        .then((res) => {
          if (!res.data?.products?.length) {
            setStatus({ type: "error", msg: res.data?.menu_error || "Menü yüklenemedi, sayfayı yenileyin." });
          } else {
            setMenu(res.data);
          }
        })
        .catch(() => setStatus({ type: "error", msg: "Menü yüklenemedi, sayfayı yenileyin." }));
    }
  }, [branch, menu]);

  const categories = useMemo(() => {
    if (!menu) return [];
    return menu.categories.filter((c) => menu.products.some((p) => p.category === c.name));
  }, [menu]);

  const visibleProducts = useMemo(() => {
    if (!menu) return [];
    if (activeCat === "all") return menu.products;
    return menu.products.filter((p) => p.category === activeCat);
  }, [menu, activeCat]);

  const makeKey = (id, drink = "", removed = [], extras = []) =>
    `${id}|${drink}|${[...removed].sort().join(",")}|${[...extras].sort().join(",")}`;

  const getLine = (key) => cart[key] || { qty: 0, productId: "", drink: "", removed: [], extras: [] };

  const setLine = (key, patch) =>
    setCart((c) => {
      const line = { ...c[key], ...patch };
      const copy = { ...c };
      if (!line.qty || line.qty <= 0) delete copy[key];
      else copy[key] = line;
      return copy;
    });

  const changeQty = (key, delta) => setLine(key, { qty: Math.min(50, getLine(key).qty + delta) });

  const addSimple = (p) => {
    const key = makeKey(p.id);
    setLine(key, { productId: p.id, qty: Math.min(50, getLine(key).qty + 1), drink: "", removed: [], extras: [] });
  };

  const updateLineOptions = (oldKey, patch) =>
    setCart((c) => {
      const line = { ...c[oldKey], ...patch };
      if (!line.qty) return c;
      const newKey = makeKey(line.productId, line.drink, line.removed, line.extras);
      const copy = { ...c };
      delete copy[oldKey];
      if (newKey !== oldKey && copy[newKey]) copy[newKey] = { ...copy[newKey], qty: copy[newKey].qty + line.qty };
      else copy[newKey] = line;
      return copy;
    });

  const openProductModal = (p) => {
    setModalProduct(p);
    setModalDraft({ qty: 1, drink: "", removed: [], extras: [] });
  };

  const modalExtraDefs = modalProduct?.extras || [];
  const modalExtrasTotal = modalExtraDefs
    .filter((e) => modalDraft.extras.includes(e.name))
    .reduce((a, e) => a + e.price, 0);
  const modalUnit = (modalProduct?.price || 0) + modalExtrasTotal;

  const confirmModal = () => {
    if (!modalProduct) return;
    const key = makeKey(modalProduct.id, modalDraft.drink, modalDraft.removed, modalDraft.extras);
    setLine(key, { productId: modalProduct.id, qty: getLine(key).qty + (modalDraft.qty || 1), drink: modalDraft.drink, removed: modalDraft.removed, extras: modalDraft.extras });
    setModalProduct(null);
  };

  const modalNeedsDrink = modalProduct && (modalProduct.drinks || []).length > 0 && !modalDraft.drink;

  const toggleRemoved = (key, item) => {
    const cur = getLine(key).removed;
    updateLineOptions(key, { removed: cur.includes(item) ? cur.filter((r) => r !== item) : [...cur, item] });
  };

  const cartLines = menu
    ? Object.entries(cart)
        .map(([key, l]) => {
          const p = menu.products.find((x) => x.id === l.productId);
          if (!p) return null;
          return { ...p, key, qty: l.qty, drink: l.drink || "", removed: l.removed || [], extrasSel: l.extras || [], extraDefs: p.extras || [] };
        })
        .filter(Boolean)
    : [];
  const lineTotal = (p) =>
    (p.price + p.extraDefs.filter((e) => p.extrasSel.includes(e.name)).reduce((a, e) => a + e.price, 0)) * p.qty;
  const total = cartLines.reduce((a, p) => a + lineTotal(p), 0);
  const totalItems = cartLines.reduce((a, p) => a + p.qty, 0);

  const fmt = (n) => `₺${n.toFixed(2).replace(".", ",")}`;

  const missingDrink = cartLines.some((p) => (p.drinks || []).length > 0 && !p.drink);

  const handlePay = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    if (totalItems === 0) return;
    if (missingDrink) {
      setStatus({ type: "error", msg: "Lütfen menüler için içecek seçimi yapın." });
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/link-orders`, {
        branch_code: branch.code,
        items: cartLines.map((p) => ({ id: p.id, qty: p.qty, drink: p.drink, removed: p.removed, extras: p.extrasSel })),
        customer_name: name,
        customer_phone: phone,
        note,
      });
      window.location.assign(data.payment_page_url);
    } catch (err) {
      setStatus({ type: "error", msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
      setPlacing(false);
    }
  };

  const handleCash = async () => {
    setStatus({ type: "", msg: "" });
    if (totalItems === 0) return;
    if (missingDrink) {
      setStatus({ type: "error", msg: "Lütfen menüler için içecek seçimi yapın." });
      return;
    }
    if (!name.trim() || !phone.trim()) {
      setStatus({ type: "error", msg: "Lütfen ad soyad ve telefon bilgilerinizi girin." });
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/link-orders/cash`, {
        branch_code: branch.code,
        items: cartLines.map((p) => ({ id: p.id, qty: p.qty, drink: p.drink, removed: p.removed, extras: p.extrasSel })),
        customer_name: name,
        customer_phone: phone,
        note,
      });
      window.location.assign(`/odeme-sonuc?durum=nakit&siparis=${data.order_id}`);
    } catch (err) {
      setStatus({ type: "error", msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
      setPlacing(false);
    }
  };

  const whatsappOrderUrl = () => {
    const lines = cartLines.map((p) => {
      let s = `${p.qty}x ${p.name} — ${fmt(lineTotal(p))}`;
      const det = [];
      if (p.drink) det.push(`İçecek: ${p.drink}`);
      if (p.removed && p.removed.length) det.push(`Çıkar: ${p.removed.join(", ")}`);
      if (p.extrasSel && p.extrasSel.length) det.push(`Ekstra: ${p.extrasSel.join(", ")}`);
      if (det.length) s += ` (${det.join("; ")})`;
      return s;
    });
    const msg = [
      `Merhaba, ${branch.name} şubesinden GEL AL siparişi vermek istiyorum:`,
      "",
      ...lines,
      "",
      `Toplam: ${fmt(total)}`,
      name.trim() ? `Ad Soyad: ${name.trim()}` : null,
      phone.trim() ? `Telefon: ${phone.trim()}` : null,
      note.trim() ? `Not: ${note.trim()}` : null,
    ]
      .filter((l) => l !== null)
      .join("\n");
    return `${SITE.whatsappUrl}?text=${encodeURIComponent(msg)}`;
  };

  const inputCls =
    "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-[#1A1A1A] outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20";

  const filteredBranches = branches.filter((b) => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return true;
    return (
      b.name.toLocaleLowerCase("tr-TR").includes(q) ||
      b.address.toLocaleLowerCase("tr-TR").includes(q) ||
      b.code.toLocaleLowerCase("tr-TR").includes(q)
    );
  });

  // ---- Kiosk tarzı menü görünümü ----
  if (branch) {
    return (
      <div data-testid="gelal-page" className="flex min-h-screen flex-col bg-[#F7F7F8]">
        <div className="bg-[#E63946]">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-8">
            <div>
              <Link to="/" data-testid="gelal-logo-link" className="flex items-center gap-3">
                <img src="/assets/logo.png" alt="Deny's Burger & Fries logosu" className="h-10 w-auto rounded-lg bg-white p-1" />
              </Link>
              <h1 className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-testid="gelal-branch-title">
                Sipariş Vermeye Başla
              </h1>
              <p className="text-xs font-medium text-white/85">
                {branch.name} · Menüden seç, sepete ekle ve siparişini tamamla.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              data-testid="gelal-cart-button"
              className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#E63946] shadow-md transition-transform hover:scale-105"
            >
              <ShoppingCart size={18} strokeWidth={2.5} />
              Sepetim{totalItems > 0 ? ` (${totalItems})` : ""}
            </button>
          </div>
        </div>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[200px_1fr]">
            <aside className="lg:sticky lg:top-6 lg:h-fit">
              <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible" data-testid="gelal-categories">
                <button
                  type="button"
                  data-testid="gelal-cat-all"
                  onClick={() => setActiveCat("all")}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                    activeCat === "all" ? "bg-[#E63946] text-white" : "bg-white text-gray-700 shadow-sm hover:text-[#E63946]"
                  }`}
                >
                  Tüm Ürünler
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`gelal-cat-${c.id}`}
                    onClick={() => setActiveCat(c.name)}
                    className={`whitespace-nowrap rounded-full px-5 py-2.5 text-left text-sm font-semibold transition-colors ${
                      activeCat === c.name ? "bg-[#E63946] text-white" : "bg-white text-gray-700 shadow-sm hover:text-[#E63946]"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </aside>

            <div>
              {status.msg && !drawerOpen && (
                <div data-testid="gelal-error-message" className="mb-4 rounded-xl border border-[#E63946] bg-[#E63946]/10 px-4 py-3 text-sm font-bold text-[#E63946]">
                  {status.msg}
                </div>
              )}
              {!menu && <p className="py-10 text-sm font-semibold text-gray-500">Menü yükleniyor...</p>}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="gelal-menu">
                {visibleProducts.map((p) => {
                  return (
                    <div key={p.id} data-testid={`gelal-product-${p.id}`} className="overflow-hidden rounded-2xl bg-white shadow-sm transition-shadow hover:shadow-md">
                      <div className="relative">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="h-44 w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="h-44 w-full bg-gray-100" />
                        )}
                        <span className="absolute left-3 top-3 rounded-full bg-[#1A1A1A] px-3 py-1 text-[11px] font-bold text-white">
                          {p.category}
                        </span>
                      </div>
                      <div className="p-4">
                        <p className="text-sm font-extrabold text-[#1A1A1A]">{p.name}</p>
                        {p.description && (
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">{p.description}</p>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-base font-extrabold text-[#E63946]">{fmt(p.price)}</span>
                          <button
                            type="button"
                            data-testid={`gelal-add-${p.id}`}
                            onClick={() => {
                              if ((p.drinks || []).length > 0 || (p.removables || []).length > 0) openProductModal(p);
                              else addSimple(p);
                            }}
                            className="flex items-center gap-1.5 rounded-full bg-[#E63946] px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
                          >
                            <Plus size={14} strokeWidth={3} />
                            Ekle
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>

        {/* Mobil sabit sepet butonu */}
        {totalItems > 0 && !drawerOpen && (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-testid="gelal-floating-cart"
            className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[#E63946] px-5 py-3.5 text-sm font-bold text-white shadow-xl"
          >
            <ShoppingCart size={18} strokeWidth={2.5} />
            Sepete Git ({totalItems}) — {fmt(total)}
          </button>
        )}

        {/* Ürün seçim penceresi (Ekle'ye basınca) */}
        {modalProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="gelal-product-modal">
            <div className="absolute inset-0 bg-black/40" onClick={() => setModalProduct(null)} />
            <div className="relative flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="relative shrink-0">
                {modalProduct.image_url ? (
                  <img src={modalProduct.image_url} alt={modalProduct.name} className="h-48 w-full object-cover" />
                ) : (
                  <div className="h-48 w-full bg-gray-100" />
                )}
                <button
                  type="button"
                  data-testid="gelal-modal-close"
                  onClick={() => setModalProduct(null)}
                  className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-600 shadow-md transition-colors hover:text-[#E63946]"
                  aria-label="Kapat"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-base font-extrabold text-[#1A1A1A]">{modalProduct.name}</p>
                  <span className="text-base font-extrabold text-[#E63946]">{fmt(modalProduct.price)}</span>
                </div>
                {modalProduct.description && (
                  <p className="mt-1 text-xs leading-relaxed text-gray-500">{modalProduct.description}</p>
                )}

                {(modalProduct.drinks || []).length > 0 && (
                  <div className="mt-4" data-testid="gelal-modal-drinks">
                    <p className="text-xs font-extrabold uppercase tracking-wide text-[#1A1A1A]">
                      İçecek Seç <span className="text-[#E63946]">*</span>
                    </p>
                    <div className="mt-2 space-y-2">
                      {modalProduct.drinks.map((d) => (
                        <button
                          key={d.name}
                          type="button"
                          data-testid={`gelal-modal-drink-${d.name}`}
                          onClick={() => setModalDraft((m) => ({ ...m, drink: m.drink === d.name ? "" : d.name }))}
                          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                            modalDraft.drink === d.name ? "border-[#E63946] bg-[#E63946]/5" : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <span className="flex items-center gap-2 text-[#1A1A1A]">
                            {d.name}
                            <span className="text-xs font-bold text-[#1A7A3D]">
                              {d.price > 0 ? `+${fmt(d.price)}` : "Ücretsiz"}
                            </span>
                          </span>
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                              modalDraft.drink === d.name ? "border-[#E63946] bg-[#E63946]" : "border-gray-300 bg-white"
                            }`}
                          >
                            {modalDraft.drink === d.name && <span className="h-2 w-2 rounded-full bg-white" />}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(modalProduct.removables || []).length > 0 && (
                  <div className="mt-4" data-testid="gelal-modal-removables">
                    <p className="text-xs font-extrabold uppercase tracking-wide text-[#1A1A1A]">Çıkarılabilir Malzemeler</p>
                    <div className="mt-2 space-y-2">
                      {modalProduct.removables.map((r) => {
                        const removed = modalDraft.removed.includes(r);
                        return (
                          <button
                            key={r}
                            type="button"
                            data-testid={`gelal-modal-remove-${r}`}
                            onClick={() =>
                              setModalDraft((m) => ({
                                ...m,
                                removed: removed ? m.removed.filter((x) => x !== r) : [...m.removed, r],
                              }))
                            }
                            className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                              removed ? "border-[#E63946] bg-[#E63946]/5 text-gray-400 line-through" : "border-gray-200 bg-white text-[#1A1A1A] hover:border-gray-300"
                            }`}
                          >
                            {r}
                            <span
                              className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                removed ? "border-[#E63946] bg-[#E63946]" : "border-gray-300 bg-white"
                              }`}
                            >
                              {removed && (
                                <svg viewBox="0 0 12 12" className="h-3 w-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M3 3l6 6M9 3l-6 6" />
                                </svg>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {modalExtraDefs.length > 0 && (
                  <div className="mt-4" data-testid="gelal-modal-extras">
                    <p className="text-xs font-extrabold uppercase tracking-wide text-[#1A1A1A]">Eklenebilir Malzemeler</p>
                    <div className="mt-2 space-y-2">
                      {modalExtraDefs.map((e) => {
                        const added = modalDraft.extras.includes(e.name);
                        return (
                          <button
                            key={e.name}
                            type="button"
                            data-testid={`gelal-modal-extra-${e.name}`}
                            onClick={() =>
                              setModalDraft((m) => ({
                                ...m,
                                extras: added ? m.extras.filter((x) => x !== e.name) : [...m.extras, e.name],
                              }))
                            }
                            className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                              added ? "border-[#1A7A3D] bg-[#1A7A3D]/5" : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <span className="text-[#1A1A1A]">
                              {e.name} <span className="text-xs font-bold text-[#E63946]">+{fmt(e.price)}</span>
                            </span>
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                                added ? "border-[#1A7A3D] bg-[#1A7A3D] text-white" : "border-gray-300 bg-white text-gray-400"
                              }`}
                            >
                              {added ? <Minus size={13} strokeWidth={3} /> : <Plus size={13} strokeWidth={3} />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
              <div className="shrink-0 border-t border-gray-200 p-4">
                {modalNeedsDrink && (
                  <p data-testid="gelal-modal-drink-warning" className="mb-2 text-center text-xs font-bold text-[#E63946]">
                    Lütfen içecek seçimi yapın.
                  </p>
                )}
                <button
                  type="button"
                  data-testid="gelal-modal-confirm"
                  onClick={confirmModal}
                  disabled={modalNeedsDrink}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E63946] px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.01] disabled:opacity-50"
                >
                  <ShoppingCart size={16} strokeWidth={2.5} />
                  Sepete Ekle — {fmt(modalUnit)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sepet çekmecesi (kiosk tarzı sağ panel) */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50" data-testid="gelal-cart-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
            <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                <h2 className="flex items-center gap-2 text-base font-extrabold text-[#1A1A1A]">
                  <ShoppingCart size={18} strokeWidth={2.5} className="text-[#E63946]" />
                  Sepetim
                </h2>
                <button type="button" data-testid="gelal-cart-close" onClick={() => setDrawerOpen(false)} className="text-gray-500 transition-colors hover:text-[#E63946]" aria-label="Sepeti kapat">
                  <X size={22} strokeWidth={2.5} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                {cartLines.length === 0 ? (
                  <p className="py-10 text-center text-sm font-medium text-gray-500" data-testid="gelal-cart-empty">
                    Sepetiniz boş. Menüden ürün ekleyin.
                  </p>
                ) : (
                  <div className="space-y-4" data-testid="gelal-summary">
                    {cartLines.map((p) => {
                      const drinks = p.drinks || [];
                      const removables = p.removables || [];
                      return (
                        <div key={p.key} className="rounded-2xl border border-gray-200 p-4" data-testid={`gelal-cart-line-${p.key}`}>
                          <div className="flex items-start gap-3">
                            {p.image_url && (
                              <img src={p.image_url} alt={p.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" loading="lazy" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-extrabold text-[#1A1A1A]">{p.name}</p>
                                <button type="button" data-testid={`gelal-cart-remove-${p.key}`} onClick={() => setLine(p.key, { qty: 0 })} className="text-gray-400 transition-colors hover:text-[#E63946]" aria-label={`${p.name} sil`}>
                                  <Trash2 size={16} strokeWidth={2.5} />
                                </button>
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <button type="button" data-testid={`gelal-cart-minus-${p.key}`} onClick={() => changeQty(p.key, -1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-[#1A1A1A]" aria-label="azalt">
                                    <Minus size={13} strokeWidth={3} />
                                  </button>
                                  <span className="w-5 text-center text-sm font-extrabold" data-testid={`gelal-cart-qty-${p.key}`}>{p.qty}</span>
                                  <button type="button" data-testid={`gelal-cart-plus-${p.key}`} onClick={() => changeQty(p.key, 1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E63946] text-white" aria-label="artır">
                                    <Plus size={13} strokeWidth={3} />
                                  </button>
                                </div>
                                <span className="text-sm font-extrabold text-[#E63946]">{fmt(lineTotal(p))}</span>
                              </div>
                            </div>
                          </div>

                          {(drinks.length > 0 || removables.length > 0) && (
                            <div className="mt-3 space-y-2 border-t border-dashed border-gray-200 pt-3">
                              {drinks.length > 0 && (
                                <div>
                                  <button
                                    type="button"
                                    data-testid={`gelal-drink-toggle-${p.key}`}
                                    onClick={() => setOpenOptions((o) => ({ ...o, [p.key]: o[p.key] === "drink" ? null : "drink" }))}
                                    className={`w-full rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                                      p.drink ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946]" : "border-gray-300 bg-white text-gray-600"
                                    }`}
                                  >
                                    {p.drink ? `İçecek: ${p.drink}` : "İçecek Seçiniz *"}
                                  </button>
                                  {p.extrasSel.length > 0 && (
                                    <p className="mt-1 text-[11px] font-bold text-[#1A7A3D]">
                                      Ekstra: {p.extrasSel.join(", ")}
                                    </p>
                                  )}
                                  {openOptions[p.key] === "drink" && (
                                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`gelal-drink-options-${p.key}`}>
                                      {drinks.map((d) => (
                                        <button
                                          key={d.name}
                                          type="button"
                                          data-testid={`gelal-drink-${p.key}-${d.name}`}
                                          onClick={() => updateLineOptions(p.key, { drink: p.drink === d.name ? "" : d.name })}
                                          className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                                            p.drink === d.name ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-gray-300 bg-white text-gray-700"
                                          }`}
                                        >
                                          {d.image_url && <img src={d.image_url} alt={d.name} className="h-5 w-5 rounded-full object-cover" loading="lazy" />}
                                          {d.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {removables.length > 0 && (
                                <div>
                                  <button
                                    type="button"
                                    data-testid={`gelal-remove-toggle-${p.key}`}
                                    onClick={() => setOpenOptions((o) => ({ ...o, [p.key]: o[p.key] === "removed" ? null : "removed" }))}
                                    className={`w-full rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                                      p.removed.length ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946]" : "border-gray-300 bg-white text-gray-600"
                                    }`}
                                  >
                                    {p.removed.length ? `Çıkarılan: ${p.removed.join(", ")}` : "Çıkarılabilir Malzemeler"}
                                  </button>
                                  {openOptions[p.key] === "removed" && (
                                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`gelal-remove-options-${p.key}`}>
                                      {removables.map((r) => (
                                        <button
                                          key={r}
                                          type="button"
                                          data-testid={`gelal-remove-${p.key}-${r}`}
                                          onClick={() => toggleRemoved(p.key, r)}
                                          className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${
                                            p.removed.includes(r) ? "border-[#E63946] bg-[#E63946] text-white line-through" : "border-gray-300 bg-white text-gray-700"
                                          }`}
                                        >
                                          {r}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {cartLines.length > 0 && (
                <form onSubmit={handlePay} className="border-t border-gray-200 px-5 py-4" data-testid="gelal-order-form">
                  <p data-testid="gelal-total" className="flex justify-between text-lg font-extrabold text-[#1A1A1A]">
                    <span>Toplam</span>
                    <span className="text-[#E63946]">{fmt(total)}</span>
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <input data-testid="gelal-name-input" type="text" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Adınız Soyadınız" />
                    <input data-testid="gelal-phone-input" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="05XX XXX XX XX" />
                  </div>
                  <input data-testid="gelal-note-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} mt-3`} placeholder="Not (isteğe bağlı)" />
                  {status.msg && drawerOpen && (
                    <div data-testid="gelal-error-message" className="mt-3 rounded-xl border border-[#E63946] bg-[#E63946]/10 px-4 py-3 text-sm font-bold text-[#E63946]">
                      {status.msg}
                    </div>
                  )}
                  {branch.payment_ready && (
                    <>
                      <button
                        data-testid="gelal-pay-button"
                        type="submit"
                        disabled={placing}
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.01] disabled:opacity-50"
                      >
                        <CreditCard size={18} strokeWidth={2.5} />
                        {placing ? "Ödemeye yönlendiriliyorsunuz..." : `Kredi Kartıyla Öde — ${fmt(total)}`}
                      </button>
                      <p className="mt-2 text-center text-[11px] font-medium text-gray-500">Ödeme iyzico güvenli ödeme sayfasında alınır.</p>
                    </>
                  )}
                  <button
                    data-testid="gelal-cash-button"
                    type="button"
                    onClick={handleCash}
                    disabled={placing}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#1A1A1A] bg-white px-6 py-4 text-sm font-bold uppercase tracking-wide text-[#1A1A1A] transition-transform hover:scale-[1.01] disabled:opacity-50"
                  >
                    <Banknote size={18} strokeWidth={2.5} />
                    {placing ? "Siparişiniz gönderiliyor..." : `Teslimde Nakit Öde — ${fmt(total)}`}
                  </button>
                  <p className="mt-2 text-center text-[11px] font-medium text-gray-500">Siparişiniz şubeye iletilir, ödemeyi geldiğinizde nakit yaparsınız.</p>
                  <a
                    data-testid="gelal-whatsapp-button"
                    href={whatsappOrderUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.01]"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.27z"/>
                    </svg>
                    WhatsApp'tan Sipariş Ver — {fmt(total)}
                  </a>
                  <p className="mt-2 text-center text-[11px] font-medium text-gray-500">Sepetiniz hazır mesaj olarak WhatsApp'a gider, ödeme teslimde yapılır.</p>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Şube seçimi ekranı ----
  return (
    <div data-testid="gelal-page" className="flex min-h-screen flex-col bg-[#FDFBF7]">
      <header className="border-b-2 border-[#1A1A1A]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" data-testid="gelal-logo-link">
            <img src="/assets/logo.png" alt="Deny's Burger & Fries logosu" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="gelal-back-link"
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#1A1A1A] transition-colors hover:text-[#E63946]"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Ana Sayfa
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-12 md:px-8">
        <p className="font-script text-2xl text-[#E63946] md:text-3xl">Gel Al</p>
        <h1 className="mt-2 text-4xl font-black uppercase tracking-tighter text-[#1A1A1A] sm:text-5xl">
          Siparişini ver,
          <br />
          gelip al.
        </h1>
        <p className="mt-3 max-w-lg text-sm font-medium text-[#4A4A4A]">
          Şubeni seç, menüden siparişini oluştur, kredi kartıyla öde — geldiğinde hazır olsun.
        </p>

        <div className="mt-8" data-testid="gelal-branch-picker">
          <h2 className="text-lg font-black uppercase tracking-tight text-[#1A1A1A]">Şubeni Bul</h2>
          <div className="relative mt-4 max-w-lg">
            <Search size={18} strokeWidth={2.5} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
            <input
              data-testid="gelal-branch-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full border-2 border-[#1A1A1A] bg-white py-3 pl-11 pr-4 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]"
              placeholder="Şube adı, ilçe veya şehir ara... (örn: Atakum)"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {loadState === "loading" && (
              <p data-testid="gelal-loading-message" className="text-sm font-bold text-[#4A4A4A]">Şubeler yükleniyor...</p>
            )}
            {loadState === "error" && (
              <div data-testid="gelal-load-error" className="sm:col-span-2">
                <p className="text-sm font-bold text-[#E63946]">Şubeler şu an yüklenemedi.</p>
                <button data-testid="gelal-retry-button" onClick={loadBranches} className="btn-chunky mt-3 bg-white px-4 py-2 text-xs uppercase tracking-wide text-[#1A1A1A]">
                  Tekrar Dene
                </button>
              </div>
            )}
            {filteredBranches.map((b) => (
              <button
                key={b.id}
                type="button"
                data-testid={`gelal-branch-${b.code.toLowerCase()}`}
                onClick={() => setBranch(b)}
                style={{ backgroundColor: branch?.code === b.code ? "#E63946" : "#FFFFFF" }}
                className={`card-print flex items-start gap-3 p-4 text-left transition-colors ${branch?.code === b.code ? "text-white" : ""}`}
              >
                <Store size={20} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${branch?.code === b.code ? "text-white" : "text-[#E63946]"}`} />
                <span>
                  <span className="block text-sm font-black uppercase tracking-tight">{b.name}</span>
                  <span className={`mt-1 block text-xs font-medium ${branch?.code === b.code ? "text-white/90" : "text-[#4A4A4A]"}`}>
                    {b.address}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {query.trim() && filteredBranches.length === 0 && (
            <p data-testid="gelal-no-branch-message" className="mt-4 text-sm font-bold text-[#4A4A4A]">
              Bu aramayla eşleşen şube bulunamadı.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
